import React, { memo, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Box, FormControlLabel, Switch } from '@mui/material';
import { realtorsApi } from 'app/services/api';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { openConfirmationDialogAction } from 'store/global/actions';

import { PropertyAndLoanTestIds } from '../../../../../PropertyAndLoan.testIds';

import {
  GRANT_ACCESS_CONFIRMATION,
  REALTOR_PORTAL_ACCESS_LABEL,
} from './RealtorPortalAccessToggle.constants';
import { realtorPortalAccessToggleStyles } from './RealtorPortalAccessToggle.styles';

// Grants the picked realtor Realtor-Portal access to the loan being edited. Only enabled once a
// realtor is selected from the directory (it carries the realtorId); a free-typed agent is not a
// portal user so there is nothing to grant. Granting is one-way here — removal is manual elsewhere.
const RealtorPortalAccessToggle = ({ realtor = null, disabled = false }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const [isGranted, setIsGranted] = useState(false);
  const [isGranting, setIsGranting] = useState(false);

  const realtorId = realtor?.id ?? null;
  const isToggleDisabled =
    disabled || isGranting || isGranted || _.isNil(realtorId) || _.isNil(applicationId);

  const grantAccess = useCallback(async () => {
    setIsGranting(true);

    try {
      await realtorsApi.grantRealtorLoanAccess({ applicationId, realtorId });
      setIsGranted(true);
    } catch {
      setIsGranted(false);
    } finally {
      setIsGranting(false);
    }
  }, [applicationId, realtorId]);

  const onToggle = useCallback(
    e => {
      if (!e.target.checked) {
        return;
      }

      dispatch(
        openConfirmationDialogAction({
          message: GRANT_ACCESS_CONFIRMATION.message,
          description: GRANT_ACCESS_CONFIRMATION.description,
          onAccept: grantAccess,
        }),
      );
    },
    [dispatch, grantAccess],
  );

  return (
    <Box sx={realtorPortalAccessToggleStyles}>
      <FormControlLabel
        control={
          <Switch
            checked={isGranted}
            data-testid={PropertyAndLoanTestIds.REALTOR_PORTAL_ACCESS_TOGGLE}
            disabled={isToggleDisabled}
            size="small"
            onChange={onToggle}
          />
        }
        data-testid={PropertyAndLoanTestIds.REALTOR_PORTAL_ACCESS_TOGGLE_CONTROL}
        label={REALTOR_PORTAL_ACCESS_LABEL}
      />
    </Box>
  );
};

RealtorPortalAccessToggle.propTypes = {
  realtor: PropTypes.object,
  disabled: PropTypes.bool,
};

export default memo(RealtorPortalAccessToggle);
